import { useEffect } from 'react'
import {
  trackRefreshSafety,
  tryAutomaticPageRefresh,
} from '@/lib/safe-page-refresh'
import { isBrowser } from '@/lib/utils/tauri'
import { toast } from 'sonner'

const VERSION_TOAST_ID = 'web-version-update'
const DEFAULT_POLL_INTERVAL_MS = 60_000

interface VersionManifest {
  version?: unknown
}

interface WebVersionNotifierProps {
  currentVersion?: string
  enabled?: boolean
  pollIntervalMs?: number
  hasPendingWork?: () => boolean
  subscribeToNavigation?: (listener: () => void) => () => void
}

const assumePendingWork = () => true

export function WebVersionNotifier({
  currentVersion = import.meta.env.VITE_APP_VERSION,
  enabled = import.meta.env.PROD && isBrowser(),
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  hasPendingWork = assumePendingWork,
  subscribeToNavigation,
}: WebVersionNotifierProps) {
  useEffect(() => {
    if (!enabled || !currentVersion) return

    let disposed = false
    let requestInFlight: Promise<void> | null = null
    const stopTracking = trackRefreshSafety(hasPendingWork)

    const checkForUpdate = () => {
      if (requestInFlight) return requestInFlight

      requestInFlight = fetch('/version.json', {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
        signal: AbortSignal.timeout(3000),
      })
        .then(async response => {
          if (!response.ok) return

          const manifest = (await response.json()) as VersionManifest | null
          const deployedVersion =
            typeof manifest?.version === 'string' ? manifest.version.trim() : ''

          if (!disposed && deployedVersion === currentVersion) {
            toast.dismiss(VERSION_TOAST_ID)
          }

          if (
            !disposed &&
            deployedVersion &&
            deployedVersion !== currentVersion
          ) {
            if (tryAutomaticPageRefresh(currentVersion)) return
            toast('A new version of Scrambl is available', {
              id: VERSION_TOAST_ID,
              description:
                'Save any unfinished work, then refresh to get the latest updates.',
              duration: Infinity,
              action: {
                label: 'Refresh',
                onClick: () => window.location.reload(),
              },
              cancel: {
                label: "What's new",
                onClick: () => window.location.assign('/whats-new'),
              },
            })
          }
        })
        .catch(() => {
          // A failed update check should never interrupt the active session.
        })
        .finally(() => {
          requestInFlight = null
        })

      return requestInFlight
    }

    const handleFocus = () => {
      void checkForUpdate()
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate()
      }
    }

    void checkForUpdate()
    const unsubscribeNavigation = subscribeToNavigation?.(() => {
      void checkForUpdate()
    })
    const intervalId = window.setInterval(checkForUpdate, pollIntervalMs)
    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      disposed = true
      stopTracking()
      unsubscribeNavigation?.()
      window.clearInterval(intervalId)
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [
    currentVersion,
    enabled,
    pollIntervalMs,
    hasPendingWork,
    subscribeToNavigation,
  ])

  return null
}
